import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  LinearProgress,
  Tabs,
  Tab,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { baseClient } from '../api/baseClient';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Upload,
  Download,
  Delete,
  Visibility,
  Edit,
  FilterList,
  Business,
  Person,
  Description,
  Image,
  PictureAsPdf,
  TableChart,
  Slideshow,
  Archive,
  CloudUpload,
  Link as LinkIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

interface Document {
  id: string;
  filename: string;
  original_filename: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  category: string | null;
  description: string | null;
  uploaded_by: string | null;
  created_at: string;
  updated_at: string;
  deal_id: string | null;
  account_id: string | null;
  contact_id: string | null;
}

interface DocumentFilters {
  search: string;
  category: string;
  document_type: string;
  account_id: string;
  business_plan_id: string;
  deal_id: string;
  contact_id: string;
}

const DocumentsPage: React.FC = () => {
  const [filters, setFilters] = useState<DocumentFilters>({
    search: '',
    category: '',
    document_type: '',
    account_id: '',
    business_plan_id: '',
    deal_id: '',
    contact_id: '',
  });
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadData, setUploadData] = useState({
    name: '',
    description: '',
    category: 'other',
    document_type: 'other',
    tags: '',
    deal_id: '',
    account_id: '',
    business_plan_id: '',
    contact_id: '',
  });

  const queryClient = useQueryClient();
  const { user, getToken } = useAuth();
  const navigate = useNavigate();

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  // Fetch documents
  const { data: documents = [], isLoading, error } = useQuery({
    queryKey: ['documents', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.account_id) params.append('account_id', filters.account_id);
      if (filters.deal_id) params.append('deal_id', filters.deal_id);
      if (filters.contact_id) params.append('contact_id', filters.contact_id);
      if (filters.category) params.append('category', filters.category);

      const endpoint = filters.search
        ? `/documents/search/?q=${encodeURIComponent(filters.search)}&${params}`
        : `/documents/?${params}`;

      return baseClient.get(endpoint);
    },
    enabled: !!user, // Only run query if user is authenticated
  });

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      return baseClient.post('/documents/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      setUploadDialogOpen(false);
      resetUploadForm();
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (documentId: string) => {
      return baseClient.delete(`/documents/${documentId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      setDeleteDialogOpen(false);
      setSelectedDocument(null);
    },
  });

  const resetUploadForm = () => {
    setUploadFile(null);
    setUploadData({
      name: '',
      description: '',
      category: 'other',
      document_type: 'other',
      tags: '',
      deal_id: '',
      account_id: '',
      business_plan_id: '',
      contact_id: '',
    });
  };

  const handleUpload = () => {
    if (!uploadFile) return;

    const formData = new FormData();
    formData.append('file', uploadFile);
    if (uploadData.description) formData.append('description', uploadData.description);
    if (uploadData.category) formData.append('category', uploadData.category);
    if (uploadData.account_id) formData.append('account_id', uploadData.account_id);
    if (uploadData.deal_id) formData.append('deal_id', uploadData.deal_id);
    if (uploadData.contact_id) formData.append('contact_id', uploadData.contact_id);

    uploadMutation.mutate(formData);
  };

  const handleDownload = async (document: Document) => {
    try {
      const token = await getToken();
      if (!token) {
        alert('Authentication required. Please log in again.');
        return;
      }
      const response = await fetch(`/api/v1/documents/${document.id}/download`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Download failed:', errorText);
        throw new Error(`Download failed: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = document.original_filename || document.filename;
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      alert(`Failed to download document: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleView = async (document: Document) => {
    try {
      const token = await getToken();
      if (!token) {
        alert('Authentication required. Please log in again.');
        return;
      }
      const response = await fetch(`/api/v1/documents/${document.id}/view`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('View failed:', errorText);
        throw new Error(`View failed: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      // Clean up after a delay to allow the window to open
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error('View failed:', error);
      alert(`Failed to view document: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.includes('pdf')) return <PictureAsPdf color="error" />;
    if (mimeType.includes('image')) return <Image color="primary" />;
    if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return <TableChart color="success" />;
    if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return <Slideshow color="warning" />;
    return <Description color="action" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getDocumentsByCategory = () => {
    const categories = {
      all: documents,
      business_plans: documents.filter(doc => doc.business_plan_id || doc.document_type === 'business_plan'),
      accounts: documents.filter(doc => doc.account_id),
      contacts: documents.filter(doc => doc.contact_id),
      unlinked: documents.filter(doc => !doc.account_id && !doc.business_plan_id && !doc.contact_id),
    };
    return categories;
  };

  const categorizedDocs = getDocumentsByCategory();
  const currentDocs = [
    categorizedDocs.all,
    categorizedDocs.business_plans,
    categorizedDocs.accounts,
    categorizedDocs.contacts,
    categorizedDocs.unlinked,
  ][activeTab];

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Documents</Typography>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Documents</Typography>
        <Alert severity="error">Failed to load documents</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Documents</Typography>
        <Button
          variant="contained"
          startIcon={<CloudUpload />}
          onClick={() => setUploadDialogOpen(true)}
        >
          Upload Document
        </Button>
      </Box>

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search documents..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={filters.document_type}
                  onChange={(e) => setFilters({ ...filters, document_type: e.target.value })}
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="business_plan">Business Plan</MenuItem>
                  <MenuItem value="contract">Contract</MenuItem>
                  <MenuItem value="proposal">Proposal</MenuItem>
                  <MenuItem value="report">Report</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  placeholder="Account ID"
                  value={filters.account_id}
                  onChange={(e) => setFilters({ ...filters, account_id: e.target.value })}
                  size="small"
                />
                <TextField
                  placeholder="Business Plan ID"
                  value={filters.business_plan_id}
                  onChange={(e) => setFilters({ ...filters, business_plan_id: e.target.value })}
                  size="small"
                />
                <TextField
                  placeholder="Contact ID"
                  value={filters.contact_id}
                  onChange={(e) => setFilters({ ...filters, contact_id: e.target.value })}
                  size="small"
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Category Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label={`All (${categorizedDocs.all.length})`} />
          <Tab label={`Business Plans (${categorizedDocs.business_plans.length})`} />
          <Tab label={`Accounts (${categorizedDocs.accounts.length})`} />
          <Tab label={`Contacts (${categorizedDocs.contacts.length})`} />
          <Tab label={`Unlinked (${categorizedDocs.unlinked.length})`} />
        </Tabs>
      </Box>

      {/* Documents Grid */}
      {currentDocs.length === 0 ? (
        <Card sx={{ textAlign: 'center', p: 4 }}>
          <Archive sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No Documents Found
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            {activeTab === 0
              ? "No documents have been uploaded yet."
              : "No documents found in this category."
            }
          </Typography>
          <Button variant="contained" onClick={() => setUploadDialogOpen(true)}>
            Upload Your First Document
          </Button>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {currentDocs.map((document) => (
            <Grid item xs={12} md={6} lg={4} key={document.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {getFileIcon(document.mime_type)}
                    <Typography variant="h6" sx={{ ml: 1, flexGrow: 1 }} noWrap>
                      {document.name}
                    </Typography>
                  </Box>

                  {document.description && (
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {document.description}
                    </Typography>
                  )}

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(document.file_size)} • {new Date(document.created_at).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })}
                    </Typography>
                  </Box>

                  {/* Linked entities */}
                  <Box sx={{ mb: 2 }}>
                    {document.account_id && (
                      <Chip
                        icon={<Business />}
                        label="Account"
                        size="small"
                        color="primary"
                        sx={{ mr: 1, mb: 1 }}
                      />
                    )}
                    {document.business_plan_id && (
                      <Chip
                        icon={<Description />}
                        label="Business Plan"
                        size="small"
                        color="secondary"
                        sx={{ mr: 1, mb: 1 }}
                      />
                    )}
                    {document.contact_id && (
                      <Chip
                        icon={<Person />}
                        label="Contact"
                        size="small"
                        color="info"
                        sx={{ mr: 1, mb: 1 }}
                      />
                    )}
                  </Box>

                  {/* Tags */}
                  {document.tags && document.tags.length > 0 && (
                    <Box>
                      {document.tags.map((tag, index) => (
                        <Chip
                          key={index}
                          label={tag}
                          size="small"
                          variant="outlined"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                    </Box>
                  )}
                </CardContent>

                <CardActions>
                  <IconButton
                    size="small"
                    onClick={() => handleView(document)}
                    title="View Document"
                  >
                    <Visibility />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDownload(document)}
                    title="Download Document"
                  >
                    <Download />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => {
                      setSelectedDocument(document);
                      setDeleteDialogOpen(true);
                    }}
                  >
                    <Delete />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Upload Document</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <input
              type="file"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setUploadFile(file);
                  setUploadData({ ...uploadData, name: file.name });
                }
              }}
              style={{ marginBottom: 16 }}
            />

            <TextField
              fullWidth
              label="Document Name"
              value={uploadData.name}
              onChange={(e) => setUploadData({ ...uploadData, name: e.target.value })}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={uploadData.description}
              onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
              sx={{ mb: 2 }}
            />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Document Type</InputLabel>
                  <Select
                    value={uploadData.document_type}
                    onChange={(e) => setUploadData({ ...uploadData, document_type: e.target.value })}
                  >
                    <MenuItem value="business_plan">Business Plan</MenuItem>
                    <MenuItem value="contract">Contract</MenuItem>
                    <MenuItem value="proposal">Proposal</MenuItem>
                    <MenuItem value="report">Report</MenuItem>
                    <MenuItem value="presentation">Presentation</MenuItem>
                    <MenuItem value="spreadsheet">Spreadsheet</MenuItem>
                    <MenuItem value="image">Image</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Tags (comma-separated)"
                  value={uploadData.tags}
                  onChange={(e) => setUploadData({ ...uploadData, tags: e.target.value })}
                />
              </Grid>
            </Grid>

            <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
              Link to Entity (Optional)
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Account ID"
                  value={uploadData.account_id}
                  onChange={(e) => setUploadData({ ...uploadData, account_id: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Business Plan ID"
                  value={uploadData.business_plan_id}
                  onChange={(e) => setUploadData({ ...uploadData, business_plan_id: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Contact ID"
                  value={uploadData.contact_id}
                  onChange={(e) => setUploadData({ ...uploadData, contact_id: e.target.value })}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={!uploadFile || uploadMutation.isPending}
          >
            {uploadMutation.isPending ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Document Details</DialogTitle>
        <DialogContent>
          {selectedDocument && (
            <Box>
              <List>
                <ListItem>
                  <ListItemIcon>{getFileIcon(selectedDocument.mime_type)}</ListItemIcon>
                  <ListItemText
                    primary={selectedDocument.name}
                    secondary={selectedDocument.description}
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemText
                    primary="File Details"
                    secondary={`${selectedDocument.file_name} • ${formatFileSize(selectedDocument.file_size)} • ${selectedDocument.mime_type}`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Created"
                    secondary={`${new Date(selectedDocument.created_at).toLocaleString()} by ${selectedDocument.created_by}`}
                  />
                </ListItem>
                {selectedDocument.tags && selectedDocument.tags.length > 0 && (
                  <ListItem>
                    <ListItemText
                      primary="Tags"
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          {selectedDocument.tags.map((tag, index) => (
                            <Chip key={index} label={tag} size="small" sx={{ mr: 0.5 }} />
                          ))}
                        </Box>
                      }
                    />
                  </ListItem>
                )}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          {selectedDocument && (
            <Button
              onClick={() => handleDownload(selectedDocument)}
              variant="contained"
              startIcon={<Download />}
            >
              Download
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Document</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedDocument?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => selectedDocument && deleteMutation.mutate(selectedDocument.id)}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentsPage;
